import type {
  DutyRequirements,
  DutyResponse,
  NurseStatusPeriod,
  NurseWithAllRelations,
} from "@dutyon/apis";
import {
  type DutyRequestWithRelations,
  mapClientError,
  queryKeys,
  removeDutyResponse,
  updateDutyResponse,
  useAuthStore,
  useHolidays,
} from "@dutyon/apis";
import { resolveWantedAssignments } from "@dutyon/apis/algorithm";
import {
  ActionIcon,
  Alert,
  Button,
  type DutyData,
  DutyEditor,
  Flex,
  Group,
  Modal,
  modals,
  NumberInput,
  notifications,
  Paper,
  Progress,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
  useStateHistory,
} from "@dutyon/ui-kit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import { Settings, Trash2 } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MdRedo, MdUndo } from "react-icons/md";
import { useRemoteGpuBatch } from "../../hooks/useRemoteGpuBatch";
import { formatDutyRequestDateKey } from "../../services/dutyRequestDate";
import {
  GPU_DUTY_DEFAULT_SAMPLES,
  GPU_DUTY_DEFAULT_WANTED_MODE,
} from "../../services/gpuDutyGenerationConfig";
import { sameRemoteGpuReview } from "../../services/remoteGpuBatch";
import {
  type RemoteGpuBoundaryState,
  remoteGpuBoundaryLabels,
  remoteGpuBoundaryMessage,
} from "../../services/remoteGpuBoundaries";
import { useDutyEditorStore } from "../../store/useDutyEditorStore";
import { DutyEditorSettingsForm, type DutyEditorSettingsValues } from "./DutyEditorSettingsForm";
import { GenerationElapsedTime } from "./GenerationElapsedTime";
import type { GpuWantedMode } from "./GpuDutyGenerateOptionsModal";

function getNurseStatusOnDate(statusPeriods: NurseStatusPeriod[], date: Date): string {
  const target = dayjs(date).startOf("day");
  const period = statusPeriods.find((p) => {
    const start = dayjs(p.startDate).startOf("day");
    const end = dayjs(p.endDate).startOf("day");
    const isAfterOrSame = target.isAfter(start) || target.isSame(start, "day");
    const isBeforeOrSame = target.isBefore(end) || target.isSame(end, "day");
    return isAfterOrSame && isBeforeOrSame;
  });
  return period?.status ?? "ACTIVE";
}

function getNurseStatusReasonOnDate(statusPeriods: NurseStatusPeriod[], date: Date): string | null {
  const status = getNurseStatusOnDate(statusPeriods, date);
  if (status === "ACTIVE") return null;

  const reasonMap: Record<string, string> = {
    PRE_JOIN: "미입사",
    LEAVE: "휴직",
    RESIGNED: "퇴사",
  };
  return reasonMap[status] ?? "근무 불가";
}

export interface DutyResponseEditorProps {
  dutyResponse: DutyResponse;
  dutyRequest: DutyRequestWithRelations;
  isFinalizing: boolean;
  onWriteStateChange: (responseId: string, pending: boolean) => void;
}

const DutyEditorSettingsFormId = "DutyEditorSettingsFormId";
const GPU_DUTY_UI_ENABLED = import.meta.env.VITE_ENABLE_GPU_DUTY_GENERATION !== "false";

function userGenerationMessage(message: string | null) {
  return message?.replace(/Mac GPU 생성/g, "근무표 생성").replace(/Mac GPU|GPU/g, "근무표 생성");
}

function createGpuRunSeed(): number {
  const values = new Uint32Array(1);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(values);
    return values[0];
  }
  return Date.now() >>> 0;
}

export function DutyResponseEditor(props: DutyResponseEditorProps) {
  const owner = useAuthStore((state) => state.user?.sub);
  const role = useAuthStore((state) => state.user?.role);
  // Reset preview and undo history together when the initiating account/role changes.
  return (
    <DutyResponseEditorContent
      key={`${props.dutyResponse.id}:${owner ?? ""}:${role ?? ""}`}
      {...props}
      owner={owner}
      role={role}
    />
  );
}
function DutyResponseEditorContent({
  dutyResponse,
  dutyRequest,
  isFinalizing,
  onWriteStateChange,
  owner,
  role,
}: DutyResponseEditorProps & { owner?: string; role?: string }) {
  const queryClient = useQueryClient();
  const nurses = (dutyRequest.targetNurses as unknown as NurseWithAllRelations[]) || [];
  const workShifts = dutyRequest.workShifts || [];
  const wanteds = dutyRequest.wanteds || [];
  const approvedWanteds = useMemo(
    () => wanteds.filter((wanted) => wanted.status === "APPROVED"),
    [wanteds],
  );
  const requirements = dutyRequest.requirements as unknown as DutyRequirements;
  const startDate = new Date(`${formatDutyRequestDateKey(dutyRequest.startDate)}T00:00:00`);
  const endDate = new Date(`${formatDutyRequestDateKey(dutyRequest.endDate)}T00:00:00`);
  const isLocked = dutyRequest.status !== "PENDING" || isFinalizing;

  // 일자별/간호사별 비활성 사유 맵 구성 (휴직, 퇴사 예정 등)
  const disabledDates = useMemo(() => {
    if (!startDate || !endDate || nurses.length === 0) return {};

    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const map: Record<string, Record<string, string>> = {};

    nurses.forEach((nurse) => {
      const periods = nurse.statusPeriods || [];
      const nurseMap: Record<string, string> = {};

      let current = start;
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dateStr = current.format("YYYY-MM-DD");
        const reason = getNurseStatusReasonOnDate(periods, current.toDate());
        if (reason) {
          nurseMap[dateStr] = reason;
        }
        current = current.add(1, "day");
      }

      if (Object.keys(nurseMap).length > 0) {
        map[nurse.id] = nurseMap;
      }
    });

    return map;
  }, [nurses, startDate, endDate]);

  const { data: holidaysData } = useHolidays({
    startDate,
    endDate,
  });

  const [scheduleData, historyHandlers, historyState] = useStateHistory<DutyData>(
    (dutyResponse.scheduleData as unknown as DutyData) || {},
  );

  const {
    mutate: handleDelete,
    isPending: isDeleting,
    isSuccess: isDeleted,
  } = useMutation({
    mutationFn: () => removeDutyResponse(dutyResponse.id),
    onSuccess: () => {
      notifications.show({
        title: "삭제 완료",
        message: "근무표가 삭제되었습니다.",
        color: "green",
      });
      return queryClient.invalidateQueries({
        queryKey: queryKeys.dutyRequests.detail(dutyRequest.id),
      });
    },
    onError: (error) => {
      notifications.show({
        title: "삭제 실패",
        message: mapClientError(error, { fallback: "근무표를 삭제하지 못했습니다." }).message,
        color: "red",
      });
    },
  });
  const isReadOnly = isLocked || isDeleting || isDeleted;

  const savedSchedule = useRef(scheduleData);
  const saveInFlight = useRef(false);
  const isCurrentOwner = () => {
    const { user, token } = useAuthStore.getState();
    return !!token && !!owner && user?.sub === owner && user.role === role;
  };
  const {
    mutate: saveSchedule,
    isPending: isSaving,
    error: saveError,
    variables,
  } = useMutation({
    scope: { id: `duty-response:${dutyResponse.id}` },
    retry: false,
    mutationFn: async (snapshot: DutyData) => {
      if (!isCurrentOwner()) throw new Error("편집을 시작한 계정으로 다시 접속해 주세요.");
      return updateDutyResponse(dutyResponse.id, { scheduleData: snapshot });
    },
    onSuccess: (updated, snapshot) => {
      savedSchedule.current = snapshot;
      if (!isCurrentOwner()) return;
      queryClient.setQueryData<DutyRequestWithRelations>(
        queryKeys.dutyRequests.detail(dutyRequest.id),
        (current) =>
          current?.dutyResponses
            ? {
                ...current,
                dutyResponses: current.dutyResponses.map((response) =>
                  response.id === updated.id ? { ...response, ...updated } : response,
                ),
              }
            : current,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dutyRequests.detail(dutyRequest.id),
      });
    },
    onSettled: () => {
      saveInFlight.current = false;
    },
  });
  const hasUnsavedChanges = !!saveError || !isEqual(scheduleData, savedSchedule.current);
  useEffect(() => {
    if (isReadOnly || isSaving || saveInFlight.current || !hasUnsavedChanges) return;
    // After an error, keep the failed input available for explicit retry.
    if (saveError && variables === scheduleData) return;
    saveInFlight.current = true;
    saveSchedule(scheduleData);
  }, [hasUnsavedChanges, isReadOnly, isSaving, saveError, saveSchedule, scheduleData, variables]);

  useLayoutEffect(() => {
    onWriteStateChange(
      dutyResponse.id,
      isDeleting || (!isLocked && !isDeleted && (isSaving || hasUnsavedChanges)),
    );
  }, [
    dutyResponse.id,
    hasUnsavedChanges,
    isDeleted,
    isDeleting,
    isLocked,
    isSaving,
    onWriteStateChange,
  ]);
  useLayoutEffect(
    () => () => onWriteStateChange(dutyResponse.id, false),
    [dutyResponse.id, onWriteStateChange],
  );

  const remoteSource = JSON.stringify({
    id: dutyRequest.id,
    response: dutyResponse.id,
    team: dutyRequest.teamId,
    start: formatDutyRequestDateKey(dutyRequest.startDate),
    end: formatDutyRequestDateKey(dutyRequest.endDate),
    status: dutyRequest.status,
    updatedAt: dutyRequest.updatedAt,
    nurses: nurses.map((nurse) => [nurse.id, nurse.preceptorType]),
    shifts: workShifts.map((shift) => shift.id),
    requirements,
  });
  const remote = useRemoteGpuBatch(
    `${dutyRequest.id}:${dutyResponse.id}`,
    remoteSource,
    isReadOnly,
    { requestId: dutyRequest.id, responseId: dutyResponse.id },
    (preview) => {
      historyHandlers.set(preview);
      notifications.show({
        color: "green",
        title: "근무표 생성 완료",
        message: "생성된 근무표를 반영하고 자동 저장합니다. 경고와 내용을 확인해 주세요.",
      });
    },
  );
  const [remoteOptionsOpened, setRemoteOptionsOpened] = useState(false);
  const [remoteSamples, setRemoteSamples] = useState(GPU_DUTY_DEFAULT_SAMPLES);
  const [isFixedMode, setIsFixedMode] = useState(false);
  const {
    showConsecutiveDutyTooltip,
    showAvoidanceDutyTooltip,
    showFiveDayConsecutiveTooltip,
    showPongDangPongDangTooltip,
    maxConsecutiveWorkDays,
    useForbiddenPatterns,
    useAvoidancePatterns,
    usePongDangPatterns,
    setShowConsecutiveDutyTooltip,
    setShowAvoidanceDutyTooltip,
    setShowFiveDayConsecutiveTooltip,
    setShowPongDangPongDangTooltip,
    setMaxConsecutiveWorkDays,
    setUseForbiddenPatterns,
    setUseAvoidancePatterns,
    setUsePongDangPatterns,
  } = useDutyEditorStore();

  const setScheduleData = historyHandlers.set;
  const canUndo = historyState.current > 0;
  const canRedo = historyState.current < historyState.history.length - 1;

  const isGenerating = remote.phase === "running";
  const canCloseGeneration =
    !isGenerating && ["idle", "terminal", "rejected"].includes(remote.task.phase);
  const [gpuWantedMode, setGpuWantedMode] = useState<GpuWantedMode>(GPU_DUTY_DEFAULT_WANTED_MODE);

  const openDeleteConfirm = () => {
    modals.openConfirmModal({
      title: "근무표 삭제",
      children: "이 근무표를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.",
      labels: { confirm: "삭제", cancel: "취소" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        if (saveInFlight.current) {
          notifications.show({
            title: "근무표 저장 중",
            message: "자동 저장이 끝난 뒤 삭제를 다시 시도해 주세요.",
            color: "yellow",
          });
          return;
        }
        handleDelete();
      },
    });
  };

  const openSettingsModal = () => {
    modals.openConfirmModal({
      title: "근무표 에디터 설정",
      centered: true,
      size: "xl",
      children: (
        <DutyEditorSettingsForm
          formId={DutyEditorSettingsFormId}
          initialValues={{
            isFixedMode,
            showConsecutiveDutyTooltip,
            showAvoidanceDutyTooltip,
            showFiveDayConsecutiveTooltip,
            showPongDangPongDangTooltip,
            maxConsecutiveWorkDays,
            useForbiddenPatterns,
            useAvoidancePatterns,
            usePongDangPatterns,
          }}
          onSubmit={(values: DutyEditorSettingsValues) => {
            setIsFixedMode(values.isFixedMode);
            setShowConsecutiveDutyTooltip(values.showConsecutiveDutyTooltip);
            setShowAvoidanceDutyTooltip(values.showAvoidanceDutyTooltip);
            setShowFiveDayConsecutiveTooltip(values.showFiveDayConsecutiveTooltip);
            setShowPongDangPongDangTooltip(values.showPongDangPongDangTooltip);
            setMaxConsecutiveWorkDays(values.maxConsecutiveWorkDays);
            setUseForbiddenPatterns(values.useForbiddenPatterns);
            setUseAvoidancePatterns(values.useAvoidancePatterns);
            setUsePongDangPatterns(values.usePongDangPatterns);
            modals.closeAll();
          }}
        />
      ),
      confirmProps: {
        type: "submit",
        form: DutyEditorSettingsFormId,
      },
      closeOnConfirm: false,
      labels: {
        confirm: "저장",
        cancel: "취소",
      },
    });
  };

  const createGenerationTargetData = (mode: GpuWantedMode): DutyData => {
    const nextData: DutyData = Object.fromEntries(
      Object.entries(scheduleData).map(([nurseId, dates]) => [nurseId, { ...dates }]),
    );
    if (mode === "current") return nextData;

    const { assignments } = resolveWantedAssignments({
      assignments: approvedWanteds.map((wanted) => ({
        nurseId: wanted.nurseId,
        date: dayjs(wanted.targetDate).format("YYYY-MM-DD"),
        workShiftId: wanted.workShiftId,
      })),
      nurseIds: nurses
        .filter((nurse) => nurse.preceptorType !== "PRECEPEE")
        .map((nurse) => nurse.id),
      workShiftIds: workShifts.map((shift) => shift.id),
      offShiftId: workShifts.find((shift) => shift.isOff)?.id,
      startDayString: dayjs(startDate).format("YYYY-MM-DD"),
      endDayString: dayjs(endDate).format("YYYY-MM-DD"),
    });
    for (const { nurseId, date, workShiftId } of assignments) {
      nextData[nurseId] = { ...(nextData[nurseId] ?? {}) };
      nextData[nurseId][date] = { workShiftId, isFixed: true };
    }

    return nextData;
  };

  const startRemoteGenerate = async () => {
    if (isReadOnly || !remote.ready || isGenerating || remote.phase !== "idle") return;
    setRemoteOptionsOpened(false);
    await remote.start({
      teamId: dutyRequest.teamId,
      sourceRequestId: dutyRequest.id,
      sourceResponseId: dutyResponse.id,
      startDayString: dayjs(startDate).format("YYYY-MM-DD"),
      endDayString: dayjs(endDate).format("YYYY-MM-DD"),
      nurseIds: nurses.filter((n) => n.preceptorType !== "PRECEPEE").map((n) => n.id),
      workShiftIds: workShifts.map((shift) => shift.id),
      requirements: structuredClone(requirements ?? {}),
      rules: {
        maxConsecutiveDays: maxConsecutiveWorkDays,
        nightConsecutiveMax: 3,
        useForbiddenPatterns,
        useAvoidancePatterns,
        usePongDangPatterns,
      },
      sampleCount: remoteSamples,
      seed: createGpuRunSeed(),
      baseData: createGenerationTargetData(gpuWantedMode),
      preserveExistingAssignments: gpuWantedMode === "current",
    });
  };
  const renderRemoteBoundary = (state: RemoteGpuBoundaryState | null) => (
    <Stack gap="xs">
      <Text size="xs" c="orange">
        {remoteGpuBoundaryMessage(state)}
      </Text>
      {state?.status === "checked" && state.details.length > 0 && (
        <>
          <ul aria-label="기간 경계 경고 상세" style={{ margin: 0, paddingLeft: 20 }}>
            {state.details.slice(0, 10).map((issue) => (
              <li key={`${issue.dayString}:${issue.nurseId}:${issue.code}`}>
                <Text size="xs">
                  {issue.dayString} ·{" "}
                  {nurses.find((nurse) => nurse.id === issue.nurseId)?.user?.name ?? "대상 간호사"}{" "}
                  · {remoteGpuBoundaryLabels[issue.code]}
                </Text>
              </li>
            ))}
          </ul>
          {state.diagnostics.totalIssueCount > 10 && (
            <Text size="xs" c="dimmed">
              나머지 {(state.diagnostics.totalIssueCount - 10).toLocaleString("ko-KR")}건은 이
              목록에 표시하지 않습니다.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
  const applyRemoteResult = async () => {
    const reviewed = await remote.review();
    if (!reviewed || !remote.isCurrent() || isReadOnly) return;
    modals.openConfirmModal({
      title: "근무표 결과 적용",
      centered: true,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            현재 편집 내용을 생성 시작 당시 입력과 완료된 기간의 결과로 바꿉니다. 중단된 기간의 새
            결과는 적용하지 않습니다. 적용된 근무표는 자동 저장됩니다.
          </Text>
          {reviewed.warningCount !== null && (
            <Text size="sm" c={reviewed.warningCount ? "orange" : "dimmed"}>
              생성 기간의 규칙 경고 {reviewed.warningCount.toLocaleString("ko-KR")}건 · 적용 후
              편집기에서 확인해 주세요.
            </Text>
          )}
          {reviewed.staffingWarnings.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={600} c="orange">
                필요 인원 불일치
              </Text>
              <ul aria-label="필요 인원 경고 상세" style={{ margin: 0, paddingLeft: 20 }}>
                {reviewed.staffingWarnings.map((warning, index) => (
                  <li key={`${warning.dayString}-${warning.workShiftId}-${index}`}>
                    <Text size="sm">
                      {warning.dayString} ·{" "}
                      {workShifts.find((shift) => shift.id === warning.workShiftId)?.symbol ??
                        "근무"}
                      {" · 필요 "}
                      {warning.required}명 / 배정 {warning.assigned}명
                    </Text>
                  </li>
                ))}
              </ul>
            </Stack>
          )}
          {reviewed.warningsTruncated && (
            <Text size="sm" c="dimmed">
              경고 목록이 일부 생략되어 있습니다. 표시된 항목이 전체 경고는 아닙니다.
            </Text>
          )}
          {reviewed.completedChunks > 1 && renderRemoteBoundary(reviewed.boundary)}
        </Stack>
      ),
      labels: { confirm: "결과 적용", cancel: "기존 표 유지" },
      onConfirm: async () => {
        const latest = await remote.review();
        if (!latest || !remote.isCurrent() || isReadOnly) return;
        if (!sameRemoteGpuReview(reviewed, latest)) {
          notifications.show({
            color: "orange",
            title: "결과를 다시 확인해 주세요",
            message:
              "완료 결과 또는 이전 근무·경계 안내가 변경되어 적용하지 않았습니다. 결과 적용을 다시 눌러 최신 내용을 확인해 주세요.",
          });
          return;
        }
        setScheduleData(latest.preview);
        remote.clear();
      },
    });
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        {/* 근무표 정보와 자동 저장 상태 */}
        <Flex justify="space-between" align="center">
          <Group align="center" gap="xs">
            <Title order={4}>근무표</Title>
            <Text size="sm" c="dimmed">
              (생성일: {dayjs(dutyResponse.createdAt).format("YYYY-MM-DD HH:mm")} | 수정일:{" "}
              {dayjs(dutyResponse.updatedAt).format("YYYY-MM-DD HH:mm")})
            </Text>
          </Group>
          {isLocked ? (
            <Text size="sm" c="dimmed">
              {isFinalizing
                ? "요청 확정 처리 중입니다."
                : "확정되거나 종료된 근무표는 읽기 전용입니다."}
            </Text>
          ) : (
            <Group gap="xs">
              <Text role="status" size="sm" c={saveError && !isSaving ? "red" : "dimmed"}>
                {isSaving || (hasUnsavedChanges && !saveError)
                  ? "자동 저장 중…"
                  : saveError
                    ? mapClientError(saveError, { fallback: "근무표를 저장하지 못했습니다." })
                        .message
                    : "자동 저장됨"}
              </Text>
              {saveError && !isSaving && (
                <Button
                  size="compact-sm"
                  variant="light"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (saveInFlight.current) return;
                    saveInFlight.current = true;
                    saveSchedule(scheduleData);
                  }}
                >
                  저장 다시 시도
                </Button>
              )}
              <Tooltip label="근무표 삭제">
                <ActionIcon
                  aria-label="근무표 삭제"
                  onClick={openDeleteConfirm}
                  loading={isDeleting}
                  disabled={isSaving || isDeleted}
                  size="lg"
                  variant="outline"
                  color="red"
                >
                  <Trash2 size={18} aria-hidden="true" />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Flex>

        {/* 근무표 에디터 */}
        <DutyEditor
          nurses={nurses}
          workShifts={workShifts}
          wanteds={wanteds}
          requirements={requirements}
          startDate={startDate}
          endDate={endDate}
          value={scheduleData}
          readOnly={isReadOnly}
          defaultIsFixed={isFixedMode}
          validationOptions={{
            showConsecutiveDuty: showConsecutiveDutyTooltip,
            showAvoidanceDuty: showAvoidanceDutyTooltip,
            showFiveDayConsecutive: showFiveDayConsecutiveTooltip,
            showPongDangPongDang: showPongDangPongDangTooltip,
          }}
          disabledDates={disabledDates}
          holidays={holidaysData?.list}
          actions={
            isReadOnly ? undefined : (
              <Group gap="xs" align="center">
                {/* 설정 버튼 */}
                <Tooltip label="설정" withArrow>
                  <ActionIcon variant="subtle" size="lg" onClick={openSettingsModal}>
                    <Settings size={18} />
                  </ActionIcon>
                </Tooltip>
                {/* 자동 생성 */}
                {GPU_DUTY_UI_ENABLED && (
                  <Button
                    color="grape"
                    variant="light"
                    disabled={!remote.ready || remote.phase !== "idle"}
                    onClick={() => setRemoteOptionsOpened(true)}
                  >
                    근무표 자동생성
                  </Button>
                )}

                {/* Undo / Redo */}
                <Tooltip label="실행 취소 (Undo)" withArrow>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    onClick={() => historyHandlers.back()}
                    disabled={!canUndo || isGenerating}
                    aria-label="실행 취소"
                  >
                    <MdUndo size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="다시 실행 (Redo)" withArrow>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    onClick={() => historyHandlers.forward()}
                    disabled={!canRedo || isGenerating}
                    aria-label="다시 실행"
                  >
                    <MdRedo size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )
          }
          onChange={setScheduleData}
        />
      </Stack>

      {/* TODO: 현재 에러가 있어서 주석처리해둠. 추후에 Textarea 에 에러 해결되면 풀것 */}
      {/* <DutyResponseCommentSection dutyResponseId={dutyResponse.id} /> */}

      <Modal
        opened={remoteOptionsOpened || remote.phase !== "idle"}
        onClose={() => {
          if (remote.phase === "idle") setRemoteOptionsOpened(false);
          else if (canCloseGeneration) remote.clear();
        }}
        closeOnClickOutside={remote.phase === "idle"}
        closeOnEscape={remote.phase === "idle"}
        withCloseButton={remote.phase === "idle" || canCloseGeneration}
        title="근무표 자동생성"
        centered
      >
        {remote.phase !== "idle" ? (
          <Stack gap="sm">
            {remote.phase === "failed" && (
              <Alert color="red" title="근무표 생성 실패">
                완료된 기간과 원래 입력을 보존했습니다. 실패한 기간부터 다시 시도할 수 있습니다.
              </Alert>
            )}
            <Stack gap="sm">
              <output aria-live="polite">
                <Text component="span" display="block" fw={600}>
                  {isGenerating ? "근무표를 생성하고 있습니다" : "생성 작업 확인"}
                </Text>
                <Text component="span" display="block" size="sm">
                  완료 {remote.completedChunks}/{remote.totalChunks}기간
                </Text>
              </output>
              {isGenerating && remote.startedAt !== null && (
                <GenerationElapsedTime startedAt={remote.startedAt} />
              )}
              <Progress
                value={remote.totalChunks ? (remote.completedChunks / remote.totalChunks) * 100 : 0}
                animated={isGenerating}
                aria-label="근무표 생성 진행률"
              />
              {remote.generation && (
                <Text size="sm" c="dimmed">
                  {remote.generation.mode === "chained"
                    ? "다음 기간은 앞서 생성한 근무를 이어받습니다."
                    : "이전에 시작한 작업은 각 기간을 독립적으로 생성하는 방식으로 복구합니다."}
                </Text>
              )}
              {remote.message && <Text size="sm">{userGenerationMessage(remote.message)}</Text>}
              {remote.task.job && (
                <Text size="sm">
                  현재 작업:{" "}
                  {
                    {
                      QUEUED: "대기",
                      RUNNING: "연산 중",
                      SUCCEEDED: "완료",
                      FAILED: "실패",
                      TIMED_OUT: "시간 초과",
                      CANCELLED: "취소",
                    }[remote.task.job.status]
                  }
                </Text>
              )}
              {remote.completedChunks > 0 && remote.warningCount !== null && (
                <Text size="sm" c={remote.warningCount ? "orange" : "dimmed"}>
                  생성 기간의 규칙 경고 {remote.warningCount.toLocaleString("ko-KR")}건 · 적용 후
                  편집기에서 확인해 주세요.
                </Text>
              )}
              {remote.completedChunks > 1 && renderRemoteBoundary(remote.boundary)}
              {remote.task.input && (
                <Text size="xs" c="dimmed">
                  {remote.task.input.startDayString}부터 {remote.task.input.duration}일 ·{" "}
                  {remote.task.input.sampleCount.toLocaleString("ko-KR")}개 후보
                </Text>
              )}
              {remote.task.message && (
                <Alert color="orange">{userGenerationMessage(remote.task.message)}</Alert>
              )}
              {(!remote.canRestore || !remote.task.canRestore) && (
                <Alert color="orange">
                  복구 정보를 보관하지 못했습니다. 결과 확인까지 이 화면을 유지해 주세요.
                </Alert>
              )}
              <Group justify="flex-end">
                {remote.phase === "failed" &&
                  remote.task.job &&
                  ["FAILED", "TIMED_OUT"].includes(remote.task.job.status) && (
                    <Button disabled={isReadOnly} onClick={() => void remote.retryFailed()}>
                      같은 조건으로 다시 시도
                    </Button>
                  )}
                {(remote.phase === "paused" || remote.task.phase === "disconnected") && (
                  <Button
                    variant="light"
                    disabled={isReadOnly}
                    onClick={() => void remote.resume()}
                  >
                    기존 작업 확인 및 이어서 진행
                  </Button>
                )}
                {remote.completedChunks > 0 && (
                  <Button disabled={isReadOnly || isGenerating} onClick={applyRemoteResult}>
                    완료된 기간 결과 적용
                  </Button>
                )}
                {(remote.phase === "running" ||
                  (remote.task.input &&
                    !["idle", "terminal", "rejected"].includes(remote.task.phase))) && (
                  <Button variant="subtle" color="red" onClick={() => void remote.cancel()}>
                    취소
                  </Button>
                )}
                {canCloseGeneration && (
                  <Button variant="default" onClick={() => remote.clear()}>
                    닫기
                  </Button>
                )}
              </Group>
            </Stack>
          </Stack>
        ) : (
          <Stack>
            <Text size="sm">
              선택한 조건으로 근무표를 만들고, 생성 결과를 편집기에 반영해 자동 저장합니다.
            </Text>
            <NumberInput
              label="후보 수"
              value={remoteSamples}
              min={1}
              max={10000}
              onChange={(value) =>
                setRemoteSamples(typeof value === "number" ? value : GPU_DUTY_DEFAULT_SAMPLES)
              }
            />
            <Select
              label="원티드 반영 방식"
              value={gpuWantedMode}
              onChange={(value) =>
                setGpuWantedMode(value === "apply-wanteds" ? "apply-wanteds" : "current")
              }
              data={[
                { value: "current", label: "현재 입력된 근무를 유지하고 빈 셀 생성" },
                { value: "apply-wanteds", label: "승인 원티드를 고정한 뒤 생성" },
              ]}
            />
            <Button
              disabled={
                !Number.isSafeInteger(remoteSamples) || remoteSamples < 1 || remoteSamples > 10000
              }
              onClick={() => void startRemoteGenerate()}
            >
              근무표 생성
            </Button>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}
